import React from 'react';
import {
  AutocompleteInput,
  FormDataConsumer,
  NumberInput,
  REDUX_FORM_NAME,
  ReferenceInput,
  SelectInput,
  TextInput,
  required,
} from 'react-admin';
import { FormHelperText } from '@material-ui/core';
import { change as changeFieldValue } from 'redux-form';
import AchievementVideoInput from './AchievementVideoInput';
import InlineSvgPreview from './components/InlineSvgPreview';
import {
  AchievementType,
  triggerTypeChoices,
  typeChoices,
  getRecommendedTimesCopy,
} from './achievementConstants';

const TypeTriggerHelperText = () => {
  return (
    <FormHelperText component="p">
      Session, Week, and Prep Session badges work with the <strong>Times</strong> trigger so you can
      set thresholds. Forum/Profile badges stay on <strong>Coded</strong> (the UI will block that
      combination) because they are provisioned directly in code.
    </FormHelperText>
  );
};

const DisallowedCodedTypes = [AchievementType.Forum, AchievementType.Profile];

const AchievementTypeInput = () => (
  <FormDataConsumer>
    {({ formData, ...rest }) => {
      const triggerValue = formData?.triggerType;
      const triggerNumeric = Number(triggerValue);
      const triggerString =
        typeof triggerValue === 'string' ? triggerValue.trim().toLowerCase() : '';
      const isCoded = triggerNumeric === 1 || triggerString === 'coded';
      const filteredChoices = isCoded
        ? typeChoices.filter((choice) => !DisallowedCodedTypes.includes(Number(choice.id)))
        : typeChoices;

      return (
        <>
          <SelectInput
            source="type"
            label="Type"
            choices={filteredChoices}
            validate={required()}
            fullWidth
            {...rest}
          />
          {isCoded && (
            <FormHelperText component="p" error>
              Forum and Profile badges are provisioned in code when trigger type is Coded. Please
              contact Engineering to modify them.
            </FormHelperText>
          )}
        </>
      );
    }}
  </FormDataConsumer>
);

const ProgramReferenceInputContent = ({ formData, dispatch, ...rest }) => {
  const showField = Number(formData?.type) === AchievementType.WorkoutRating;
  const programId = formData?.programId;

  React.useEffect(() => {
    if (!dispatch) return;
    const hasProgramValue = typeof programId !== 'undefined' && programId !== null;

    if (!showField && hasProgramValue) {
      dispatch(changeFieldValue(REDUX_FORM_NAME, 'programId', null));
    }
  }, [dispatch, programId, showField]);

  if (!showField) {
    return null;
  }

  return (
    <>
      <ReferenceInput
        source="programId"
        reference="programs"
        resource="achievements"
        label="Program"
        perPage={25}
        sort={{ field: 'name', order: 'ASC' }}
        allowEmpty
        {...rest}
      >
        <AutocompleteInput
          optionText={(record) =>
            record?.name ? `${record.name} (#${record.id})` : `Program #${record?.id}`
          }
          optionValue="id"
          fullWidth
        />
      </ReferenceInput>
      <FormHelperText component="p">
        Program selection is only required for Workout Rating achievements.
      </FormHelperText>
    </>
  );
};

const ProgramReferenceInput = () => (
  <FormDataConsumer>{(props) => <ProgramReferenceInputContent {...props} />}</FormDataConsumer>
);

const SessionReferenceInput = () => (
  <FormDataConsumer>
    {({ ...rest }) => {
      const formatProgramLabel = (record) => {
        if (record?.program?.name) {
          return ` • ${record.program.name}`;
        }
        if (record?.programId) {
          return ` • Program #${record.programId}`;
        }
        return '';
      };

      return (
        <>
          <ReferenceInput
            source="sessionId"
            reference="sessions"
            resource="achievements"
            label="Target Session"
            perPage={25}
            sort={{ field: 'name', order: 'ASC' }}
            allowEmpty
            {...rest}
          >
            <AutocompleteInput
              optionText={(record) => {
                const programLabel = formatProgramLabel(record);
                return record?.name
                  ? `${record.name}${programLabel} (#${record.id})`
                  : `Session #${record?.id}`;
              }}
              optionValue="id"
              filterToQuery={(searchText) => ({ q: searchText })}
              fullWidth
            />
          </ReferenceInput>
          <FormHelperText component="p">
            Earned the first time this session is completed. Leave blank to keep global session
            thresholds.
          </FormHelperText>
        </>
      );
    }}
  </FormDataConsumer>
);

const TimesValueInputContent = ({ formData, dispatch, ...rest }) => {
  const triggerRaw = formData?.triggerType;

  const isTimes = Number(triggerRaw) === 2;
  const hasSessionTarget = Number(formData?.sessionId) > 0;

  const hasTimesValue = typeof formData?.timesValue !== 'undefined' && formData.timesValue !== null;

  React.useEffect(() => {
    if (!dispatch) return;

    if (!isTimes && hasTimesValue) {
      dispatch(changeFieldValue(REDUX_FORM_NAME, 'timesValue', null));
    }
  }, [dispatch, hasTimesValue, isTimes]);

  React.useEffect(() => {
    if (!dispatch) return;

    if (hasSessionTarget && formData?.timesValue !== 1) {
      dispatch(changeFieldValue(REDUX_FORM_NAME, 'timesValue', 1));
    }
  }, [dispatch, formData?.timesValue, hasSessionTarget]);

  let helperCopy = 'Only required when Trigger Type is Times.';
  if (hasSessionTarget) {
    helperCopy = 'Locked to 1 for session-specific achievements (first completion).';
  }

  const recommendedCopy = isTimes && !hasSessionTarget ? getRecommendedTimesCopy(triggerRaw) : null;
  const helperText = (
    <>
      {helperCopy}
      {recommendedCopy ? (
        <>
          <br />
          {recommendedCopy}
        </>
      ) : null}
    </>
  );

  return (
    <NumberInput
      source="timesValue"
      label="Times Value"
      disabled={!isTimes || hasSessionTarget}
      helperText={helperText}
      validate={!hasSessionTarget && isTimes ? [required()] : []}
      fullWidth
      {...rest}
    />
  );
};

const TimesValueInput = () => (
  <FormDataConsumer>{(props) => <TimesValueInputContent {...props} />}</FormDataConsumer>
);

const InlineSvgPreviewField = () => (
  <FormDataConsumer>
    {({ formData }) => <InlineSvgPreview value={formData?.icon} />}
  </FormDataConsumer>
);

const AchievementFormFields = () => (
  <>
    <TextInput source="name" validate={required()} fullWidth />
    <TextInput source="description" multiline validate={required()} fullWidth />
    <SelectInput
      source="triggerType"
      label="Trigger Type"
      choices={triggerTypeChoices}
      validate={required()}
      fullWidth
    />
    <TypeTriggerHelperText />
    <AchievementTypeInput />
    <ProgramReferenceInput />
    <SessionReferenceInput />
    <TimesValueInput />
    <TextInput
      source="icon"
      label="Achievement Icon (SVG)"
      multiline
      helperText="Paste inline SVG. Scripts and foreign objects are stripped automatically."
      fullWidth
    />
    <InlineSvgPreviewField />
    <AchievementVideoInput
      helperText="Choose an existing upload from Media → Videos. Upload new videos there."
      showPreview
    />
  </>
);

export default AchievementFormFields;
